import React, { useState, useEffect } from 'react'
import { Box, IconButton } from '@mui/material'
import FileUploadIcon from '@mui/icons-material/FileUpload'
import Typography from '@/v2/components/common/Typography'
import { calculateChecksum } from './calculateCheckSum'

async function uploadFile(file) {
  const response = await fetch('/rails/active_storage/direct_uploads', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')
        ?.content,
    },
    body: JSON.stringify({
      blob: {
        filename: file.name,
        content_type: file.type,
        byte_size: file.size,
        checksum: await calculateChecksum(file),
      },
    }),
  })

  const directUploadData = await response.json()

  await fetch(directUploadData.direct_upload.url, {
    method: 'PUT',
    headers: directUploadData.direct_upload.headers,
    body: file,
  })

  return directUploadData.signed_id
}

export default function InsuranceCardUploads({
  frontUrl,
  backUrl,
  onUpload,
  errors,
}) {
  const [localFrontPreview, setLocalFrontPreview] = useState(null)
  const [localBackPreview, setLocalBackPreview] = useState(null)
  const [frontSignedId, setFrontSignedId] = useState(null)
  const [backSignedId, setBackSignedId] = useState(null)

  useEffect(() => {
    if (frontUrl && !localFrontPreview) {
      setLocalFrontPreview(frontUrl)
    }
  }, [frontUrl, localFrontPreview])

  useEffect(() => {
    if (backUrl && !localBackPreview) {
      setLocalBackPreview(backUrl)
    }
  }, [backUrl, localBackPreview])

  const handleFrontChange = async e => {
    const file = e.target.files[0]
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      setLocalFrontPreview(previewUrl)
      const signedId = await uploadFile(file)
      setFrontSignedId(signedId)
      onUpload && onUpload({ front: signedId, back: backSignedId })
    }
  }

  const handleBackChange = async e => {
    const file = e.target.files[0]
    if (file) {
      const previewUrl = URL.createObjectURL(file)
      setLocalBackPreview(previewUrl)
      const signedId = await uploadFile(file)
      setBackSignedId(signedId)
      onUpload && onUpload({ front: frontSignedId, back: signedId })
    }
  }

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      gap={2}
      sx={{ width: '100%' }}
    >
      <Box
        sx={{
          flex: 1,
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          Insurance ID Card - Front
        </Typography>
        <IconButton
          color="secondary"
          component="label"
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            // Override hover style
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {localFrontPreview ? (
            <Box
              component="img"
              src={localFrontPreview}
              alt="Insurance front"
              sx={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
            />
          ) : (
            <>
              <FileUploadIcon fontSize="large" color="secondary" />
              <Typography variant="body2" mt={1}>
                Upload
              </Typography>
            </>
          )}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleFrontChange}
          />
        </IconButton>
        {errors?.insurance_card_front && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1, width: '100%', textAlign: 'center' }}
          >
            {errors.insurance_card_front}
          </Typography>
        )}
      </Box>

      {/* Back Image Card */}
      <Box
        sx={{
          flex: 1,
          border: '2px dashed #ccc',
          borderRadius: 2,
          p: 2,
          textAlign: 'center',
        }}
      >
        <Typography variant="subtitle1" gutterBottom>
          Insurance ID Card - Back
        </Typography>
        <IconButton
          color="secondary"
          component="label"
          sx={{
            display: 'inline-flex',
            flexDirection: 'column',
            // Override hover style
            '&:hover': {
              backgroundColor: 'transparent',
            },
          }}
        >
          {localBackPreview ? (
            <Box
              component="img"
              src={localBackPreview}
              alt="Insurance back"
              sx={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
            />
          ) : (
            <>
              <FileUploadIcon fontSize="large" color="secondary" />
              <Typography variant="body2" mt={1}>
                Upload
              </Typography>
            </>
          )}
          <input
            type="file"
            hidden
            accept="image/*"
            onChange={handleBackChange}
          />
        </IconButton>
        {errors?.insurance_card_back && (
          <Typography
            variant="body2"
            color="error"
            sx={{ mt: 1, width: '100%', textAlign: 'center' }}
          >
            {errors.insurance_card_back}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
